/* eslint-disable */
// @ts-nocheck
/* Patrianna request helpers: HMAC signing and encrypted response handling */
/* Used by hey-api generated client entry; do not depend on hey-api core. */
import crypto from 'crypto';
import Base64 from 'crypto-js/enc-base64';
import hmacSHA256 from 'crypto-js/hmac-sha256';

/** Node crypto with createDecipheriv (algorithm string accepted for aes-256-cfb8). */
interface CryptoWithDecipheriv {
  createDecipheriv(
    algorithm: string,
    key: crypto.BinaryLike,
    iv: crypto.BinaryLike,
  ): crypto.Decipher;
}

// Obfuscated header names (XOR key: 42, then base64)
const OBFUSCATED_HEADERS = {
  NONCE: 'UgdERURJTw==',
  TIMESTAMP: 'cgd+Q0dPWV5LR1o=',
  CF_CHECK: 'UgdJTAdJQkE=',
} as const;

const XOR_KEY = 42;

function deobfuscateString(obfuscated: string): string {
  try {
    return atob(obfuscated)
      .split('')
      .map((char) => String.fromCharCode(char.charCodeAt(0) ^ XOR_KEY))
      .join('');
  } catch {
    return obfuscated;
  }
}

function generateRandomString(length: number): string {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(
      Math.floor(Math.random() * characters.length),
    );
  }
  return result;
}

/**
 * Build HMAC signing headers for the given URL when urlSigningKey and baseUrl are set.
 */
export function getHMACHeaders(
  url: string,
  urlSigningKey: string | undefined,
  baseUrl: string | undefined,
): Record<string, string> {
  if (!urlSigningKey || !baseUrl || !url.includes(baseUrl)) {
    return {};
  }
  const { pathname, search } = new URL(url);
  const pathWithQueryParams = `${pathname}${search}`;
  const now = Math.floor(Date.now() / 1000);
  const randomAlphabetic = generateRandomString(16);
  const digest = `${pathWithQueryParams}${randomAlphabetic}${now}`;
  const hmac = encodeURIComponent(
    Base64.stringify(hmacSHA256(digest, urlSigningKey)),
  );

  return {
    [deobfuscateString(OBFUSCATED_HEADERS.NONCE)]: randomAlphabetic,
    [deobfuscateString(OBFUSCATED_HEADERS.CF_CHECK)]: hmac,
    [deobfuscateString(OBFUSCATED_HEADERS.TIMESTAMP)]: now.toString(),
  };
}

/**
 * Build X-Payload-Var header for encrypted endpoints when URL contains one of encryptedMethods.
 */
export function getEncryptedHeader(
  url: string,
  encryptedMethods: string[],
): Record<string, string> {
  const urlObj = new URL(url);
  const brand = urlObj.searchParams.get('brandName');
  const needsEncryption =
    encryptedMethods.length > 0 &&
    encryptedMethods.some((method) => url.includes(method));
  return needsEncryption && brand ? { 'X-Payload-Var': brand } : {};
}

/**
 * Decrypt response body when X-Payload-Var is present (AES-256-cfb8).
 */
export async function decodeResponse(response: Response): Promise<unknown> {
  const encryptionKey = response.headers.get('X-Payload-Var') || '';
  const keyBytes = new Uint8Array(
    crypto
      .createHash('sha256')
      .update(encryptionKey.split('').reverse().join(''))
      .digest(),
  );
  const initializationVector = keyBytes.subarray(0, 16);

  const decipher = (crypto as CryptoWithDecipheriv).createDecipheriv(
    'aes-256-cfb8',
    keyBytes,
    initializationVector,
  );

  const dataString = await response.text();
  const decrypted = decipher.update(dataString, 'base64', 'utf8');

  return JSON.parse(decrypted);
}
